import { useCallback, useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { seopService } from '@/services/seop.service';
import type { SeopCalc, SeopScenario } from '@/types/seop';

interface CenarioAberto {
  /** O cenário salvo que a tela está mostrando; `null` = plano atual. */
  aberto: SeopScenario | null;
  /** O cálculo do snapshot para o mês selecionado; `null` enquanto carrega. */
  calcAberto: SeopCalc | null;
  abrir: (s: SeopScenario) => void;
  fechar: () => void;
}

/**
 * Abrir um cenário salvo **para leitura**.
 *
 * Usa o `calc-from-scenario` (o mesmo da comparação), que roda o motor sobre o snapshot e
 * não toca no plano atual. Trocar o mês no seletor recalcula o mesmo cenário para o mês
 * novo. Se a leitura falha, volta para o plano atual em vez de deixar a tela vazia.
 */
export function useSeopCenarioAberto(month: number): CenarioAberto {
  const { showToast } = useToast();
  const [aberto, setAberto] = useState<SeopScenario | null>(null);
  const [calcAberto, setCalcAberto] = useState<SeopCalc | null>(null);

  useEffect(() => {
    setCalcAberto(null);
    if (!aberto) return undefined;
    let vigente = true;
    seopService
      .getCalcFromScenario(aberto.id, month)
      .then((c) => {
        if (vigente) setCalcAberto(c);
      })
      .catch(() => {
        if (!vigente) return;
        showToast('Não consegui abrir o cenário. Você continua no plano atual.', 'error');
        setAberto(null);
      });
    return () => {
      vigente = false;
    };
  }, [aberto, month, showToast]);

  const fechar = useCallback(() => setAberto(null), []);
  return { aberto, calcAberto, abrir: setAberto, fechar };
}
